import { useMemo } from 'react'
import { createClient, Provider as GQLProvider, fetchExchange } from 'urql'
import { authExchange } from '@urql/exchange-auth'
import { cacheExchange } from '@urql/exchange-graphcache'
import { persistedExchange } from '@urql/exchange-persisted'

import { useAuth } from './AuthProvider'
import { Auth } from './auth'
import { environment } from './environment'
import { schema } from './generated/schema'

const authApi = new Auth()

export const GqlProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { auth, setAuth, decodeToken } = useAuth()

  const client = useMemo(() => {
    const jwt = auth?.token
    return createClient({
      url: `${environment.API_URL}/admin/graphql`,
      fetchOptions: () => ({
        headers: { Authorization: jwt ? `Bearer ${jwt}` : '' },
      }),
      exchanges: [
        cacheExchange({
          schema: schema,
          keys: {
            Group: (data) => data.groupId as string,
            User: (data) => data.id as string,
            GroupStats: () => null, // Stats are query-specific, no caching needed
            DashboardStats: () => null, // Single entity, no caching needed
            MonthlyVolume: () => null, // Scoped by getMonthlyVolumes filter; do not normalize by month
            MintVolume: () => null, // Child of MonthlyVolume; volumes differ per filter scope
            // Paginated Connection types - cache by their paginated results
            AdminTransactionsConnection: () => null, // Don't cache connections - cache individual nodes instead
            AdminUsersConnection: () => null,
            PageInfo: () => null, // Don't cache page info
          },
          updates: {
            Mutation: {
              // User management mutations
              createUser: (_result, _args, cache) => {
                console.warn('🔧 Cache invalidation triggered for createUser')

                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getUsers')
                cache.invalidate('Query', 'getUser')

                // Invalidate all getUsers queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getUsers: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getUsers',
                )

                // Force complete cache refresh for user-related queries
                cache.invalidate('Query', 'getDashboardStats')
                cache.invalidate('Query', 'getGroupStats')

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },
              createGroupOwner: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getUsers')
                cache.invalidate('Query', 'getUser')

                // Invalidate all getUsers queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getUsers: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getUsers',
                )

                // Force complete cache refresh for user-related queries
                cache.invalidate('Query', 'getDashboardStats')
                cache.invalidate('Query', 'getGroupStats')

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },
              addUserToGroup: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getUsers')
                cache.invalidate('Query', 'getUser')

                // Invalidate all getUsers queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getUsers: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getUsers',
                )

                // Force complete cache refresh for user-related queries
                cache.invalidate('Query', 'getDashboardStats')
                cache.invalidate('Query', 'getGroupStats')
                cache.invalidate('Query', 'getGroups')

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },
              removeUserFromGroup: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getUsers')
                cache.invalidate('Query', 'getUser')
                cache.invalidate('Query', 'getGroups')

                // Invalidate all getUsers queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getUsers: () => true,
                })

                // Invalidate all getGroups queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getGroups: () => true,
                })

                // Force complete cache refresh for user-related queries
                cache.invalidate('Query', 'getDashboardStats')
                cache.invalidate('Query', 'getGroupStats')

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },
              createSuperAdmin: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getUsers')
                cache.invalidate('Query', 'getUser')

                // Invalidate all getUsers queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getUsers: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getUsers',
                )

                // Force complete cache refresh for user-related queries
                cache.invalidate('Query', 'getDashboardStats')
                cache.invalidate('Query', 'getGroupStats')

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },
              updateUser: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getUsers')
                cache.invalidate('Query', 'getUser')

                // Invalidate all getUsers queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getUsers: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getUsers',
                )

                // Force complete cache refresh for user-related queries
                cache.invalidate('Query', 'getDashboardStats')
                cache.invalidate('Query', 'getGroupStats')

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },
              // NOTE: These don't effect the cache
              // resetPassword: (_result, _args, cache) => {
              //   cache.invalidate('Query', 'getUsers')
              // },
              // resetMFA: (_result, _args, cache) => {
              //   cache.invalidate('Query', 'getUsers')
              // },

              // Group management mutations
              createGroup: (_result, _args, cache) => {
                console.warn('🔧 Cache invalidation triggered for createGroup')

                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getGroups')
                cache.invalidate('Query', 'getGroupStats')
                cache.invalidate('Query', 'getDashboardStats')

                // Invalidate all queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getGroups: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getGroups',
                )

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },
              updateGroup: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getGroups')
                cache.invalidate('Query', 'getGroup')
                cache.invalidate('Query', 'getCompanies')
                cache.invalidate('Query', 'getGroupStats')
                cache.invalidate('Query', 'getDashboardStats')

                // Invalidate all queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getGroups: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getGroups',
                )

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },
              updateGroupLiquidityProvider: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getGroups')
                cache.invalidate('Query', 'getGroupStats')
                cache.invalidate('Query', 'getDashboardStats')

                // Invalidate all queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getGroups: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getGroups',
                )

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },

              // Company management mutations
              createCompany: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getCompanies')
                cache.invalidate('Query', 'getCompany')
                cache.invalidate('Query', 'getDashboardStats')
                cache.invalidate('Query', 'getGroupStats')

                // Invalidate all queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getCompanies: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getCompanies',
                )

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },
              updateCompany: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getCompanies')
                cache.invalidate('Query', 'getCompany')
                cache.invalidate('Query', 'getCompanyStats')
                cache.invalidate('Query', 'getDashboardStats')
                cache.invalidate('Query', 'getGroupStats')

                // Invalidate all queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getCompanies: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getCompany: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getCompanies',
                )
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getCompany',
                )

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },
              updateCompanyDocument: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getCompanyDocuments')
                cache.invalidate('Query', 'getCompany')
                cache.invalidate('Query', 'getCompanyStats')

                // Invalidate all queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getCompanyDocuments: () => true,
                })

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },

              // Compliance officer mutations
              createComplianceOfficer: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getComplianceOfficers')

                // Invalidate all queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getComplianceOfficers: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getComplianceOfficers',
                )

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },

              // Transaction mutations - separated into deposit and swap specific mutations
              flagDeposit: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getTransactions')
                cache.invalidate('Query', 'getDepositTransaction')
                cache.invalidate('Query', 'getDashboardStats')
                cache.invalidate('Query', 'getGroupStats')
                cache.invalidate('Query', 'getCompanyStats')

                // Invalidate all paginated queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getTransactions: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getDepositTransaction: () => true,
                })

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },
              flagSwap: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getTransactions')
                cache.invalidate('Query', 'getSwaps')
                cache.invalidate('Query', 'getDashboardStats')
                cache.invalidate('Query', 'getGroupStats')
                cache.invalidate('Query', 'getCompanyStats')

                // Invalidate all paginated queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getTransactions: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getSwaps: () => true,
                })

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },
              approveDeposit: (_result, _args, cache) => {
                console.warn('🔧 Cache invalidation triggered for approveDeposit')

                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getTransactions')
                cache.invalidate('Query', 'getDepositTransaction')
                cache.invalidate('Query', 'getDashboardStats')
                cache.invalidate('Query', 'getGroupStats')
                cache.invalidate('Query', 'getCompanyStats')

                // Invalidate all paginated queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getTransactions: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getDepositTransaction: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getTransactions',
                )

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },
              rejectDeposit: (_result, _args, cache) => {
                console.warn('🔧 Cache invalidation triggered for rejectDeposit')

                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getTransactions')
                cache.invalidate('Query', 'getDepositTransaction')
                cache.invalidate('Query', 'getDashboardStats')
                cache.invalidate('Query', 'getGroupStats')
                cache.invalidate('Query', 'getCompanyStats')

                // Invalidate all paginated queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getTransactions: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getDepositTransaction: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getTransactions',
                )

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },

              // Gas wallet mutations
              refreshGasWallets: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getGasWallets')

                // Invalidate all queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getGasWallets: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getGasWallets',
                )

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },

              // WhitelistedAddress mutations
              approveWhitelistedAddress: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getCompanyWhitelistedAddresses')

                // Invalidate all queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getCompanyWhitelistedAddresses: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getCompanyWhitelistedAddresses',
                )

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },
              rejectWhitelistedAddress: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getCompanyWhitelistedAddresses')

                // Invalidate all queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getCompanyWhitelistedAddresses: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getCompanyWhitelistedAddresses',
                )

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },
              updateWhitelistedAddress: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getCompanyWhitelistedAddresses')

                // Invalidate all queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getCompanyWhitelistedAddresses: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getCompanyWhitelistedAddresses',
                )

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },
              createWhitelistedAddress: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getCompanyWhitelistedAddresses')

                // Invalidate all queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getCompanyWhitelistedAddresses: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getCompanyWhitelistedAddresses',
                )

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },

              // Company reassignment mutation
              reassignCompany: (_result, _args, cache) => {
                cache.invalidate('Query', 'getCompany')
                cache.invalidate('Query', 'getCompanies')
                cache.invalidate('Query', 'getDashboardStats')
                cache.invalidate('Query', 'getGroupStats')
                cache.invalidate('Query', 'getCompanyStats')
                cache.invalidate('Query', 'getTransactions')
                cache.invalidate('Query', 'getSwaps')
                cache.invalidate('Query', 'getCompanyWhitelistedAddresses')
                cache.invalidate('Query', 'getMonthlyVolumes')

                cache.invalidate({
                  __typename: 'Query',
                  getCompany: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getCompanies: () => true,
                })

                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getCompany',
                )
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getCompanies',
                )

                cache.invalidate('Query')
              },

              // User-Company assignment mutations
              assignUserToCompany: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getCompany')
                cache.invalidate('Query', 'getCompanies')
                cache.invalidate('Query', 'getUsers')
                cache.invalidate('Query', 'getUser')
                cache.invalidate('Query', 'getDashboardStats')
                cache.invalidate('Query', 'getGroupStats')
                cache.invalidate('Query', 'getCompanyStats')

                // Invalidate all queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getCompany: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getCompanies: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getUsers: () => true,
                })

                // Invalidate specific fields on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getCompany',
                )
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getCompanies',
                )
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getUsers',
                )

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },
              assignUserToCompanies: (_result, _args, cache) => {
                console.warn(
                  '🔧 Cache invalidation triggered for assignUserToCompanies (bulk assignment)',
                )

                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getCompany')
                cache.invalidate('Query', 'getCompanies')
                cache.invalidate('Query', 'getUsers')
                cache.invalidate('Query', 'getUser')
                cache.invalidate('Query', 'getDashboardStats')
                cache.invalidate('Query', 'getGroupStats')
                cache.invalidate('Query', 'getCompanyStats')

                // Invalidate all queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getCompany: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getCompanies: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getUsers: () => true,
                })

                // Invalidate specific fields on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getCompany',
                )
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getCompanies',
                )
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getUsers',
                )
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getUser',
                )

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },
              removeUserFromCompany: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getCompany')
                cache.invalidate('Query', 'getCompanies')
                cache.invalidate('Query', 'getUsers')
                cache.invalidate('Query', 'getUser')
                cache.invalidate('Query', 'getDashboardStats')
                cache.invalidate('Query', 'getGroupStats')
                cache.invalidate('Query', 'getCompanyStats')

                // Invalidate all queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getCompany: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getCompanies: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getUsers: () => true,
                })

                // Invalidate specific fields on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getCompany',
                )
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getCompanies',
                )
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getUsers',
                )

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },

              // Auth mutations
              logout: (_result, _args, cache) => {
                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getMe')

                // Clear entire Query cache for logout (user session ending)
                cache.invalidate('Query')
              },

              // Swap management mutations
              approveSwap: (_result, _args, cache) => {
                console.warn('🔧 Cache invalidation triggered for approveSwap')

                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getSwaps')
                cache.invalidate('Query', 'getTransactions')
                cache.invalidate('Query', 'getDashboardStats')
                cache.invalidate('Query', 'getGroupStats')
                cache.invalidate('Query', 'getCompanyStats')

                // Invalidate all paginated queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getSwaps: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getTransactions: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getTransactions',
                )

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },
              rejectSwap: (_result, _args, cache) => {
                console.warn('🔧 Cache invalidation triggered for rejectSwap')

                // Most aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getSwaps')
                cache.invalidate('Query', 'getTransactions')
                cache.invalidate('Query', 'getDashboardStats')
                cache.invalidate('Query', 'getGroupStats')
                cache.invalidate('Query', 'getCompanyStats')

                // Invalidate all paginated queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getSwaps: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getTransactions: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getTransactions',
                )

                // Clear entire Query cache as last resort (most aggressive)
                cache.invalidate('Query')
              },
              createDepositNote: (_result, _args, cache) => {
                // Aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getDepositTransaction')

                // Invalidate all getDepositTransaction queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getDepositTransaction: () => true,
                })

                // Invalidate specific field on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getDepositTransaction',
                )
              },
              createSwapNote: (_result, _args, cache) => {
                // Aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getSwap')
                cache.invalidate('Query', 'GetSwapTransaction')

                // Invalidate all getSwap queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  getSwap: () => true,
                })

                // Invalidate all GetSwapTransaction queries regardless of variables
                cache.invalidate({
                  __typename: 'Query',
                  GetSwapTransaction: () => true,
                })

                // Invalidate specific fields on Query type
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'getSwap',
                )
                cache.invalidate(
                  {
                    __typename: 'Query',
                  },
                  'GetSwapTransaction',
                )
              },
            },
          },
        }),
        authExchange(async (utils) => ({
          addAuthToOperation(operation) {
            // LOG THE API CALL
            // const definition = operation?.query?.definitions[0]
            // if (definition?.kind === 'OperationDefinition' && definition?.name?.kind === 'Name') {
            //   const { value } = definition.name
            //   console.warn('API Call: ', value)
            // }

            if (!jwt) return operation
            return utils.appendHeaders(operation, {
              Authorization: `Bearer ${jwt}`,
            })
          },
          willAuthError() {
            let _isJwtStale: boolean = true
            if (jwt) {
              _isJwtStale = authApi.isJwtStale(jwt)
              if (_isJwtStale === true) {
                return true
              } else {
                return false
              }
            }
            return true
          },
          didAuthError(error) {
            const errorStr = error?.toString() || ''
            const errorMessage = error?.message || ''

            // Check for various JWT authentication errors
            const authErrors = [
              'Unauthorized',
              'JWT verification failed',
              'Token expired',
              'Invalid token',
              'Authentication failed',
              'Account has been deactivated',
            ]

            // Check error string and message for auth errors
            const hasAuthError = authErrors.some(
              (authError) => errorStr.includes(authError) || errorMessage.includes(authError),
            )

            // Check for 401 status code in various error formats
            const has401Status =
              errorStr.includes('401') ||
              errorMessage.includes('401') ||
              error?.response?.status === 401

            // Check for USER_INACTIVE error code specifically
            const isUserInactive =
              errorStr.includes('USER_INACTIVE') || errorMessage.includes('USER_INACTIVE')

            if (hasAuthError || has401Status || isUserInactive) {
              if (isUserInactive) {
                console.warn('User account deactivated:', {
                  error: errorStr,
                  message: errorMessage,
                })
              } else {
                console.warn('Authentication error detected:', {
                  error: errorStr,
                  message: errorMessage,
                })
              }
              return true
            } else {
              console.error('Non-auth error:', error)
              return false
            }
          },
          async refreshAuth() {
            try {
              if (auth?.refreshToken) {
                const res = await authApi.refresh(auth.refreshToken)
                const role = decodeToken(res.token)
                setAuth({ ...res, role: role.role, userId: role.userId })
              } else {
                setAuth(null)
              }
            } catch (e) {
              console.error('Failed to refresh token', e)
              setAuth(null)
            }
          },
        })),
        persistedExchange({
          enforcePersistedQueries: true,
          enableForMutation: true,
          generateHash: async (query: string) => {
            // Generate SHA256 hash of the query string
            const encoder = new TextEncoder()
            const data = encoder.encode(query)
            const hashBuffer = await crypto.subtle.digest('SHA-256', data)
            const hashArray = Array.from(new Uint8Array(hashBuffer))
            return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
          },
        }),
        fetchExchange,
      ],
    })
  }, [auth?.refreshToken, auth?.token, setAuth, decodeToken])
  return <GQLProvider value={client}>{children}</GQLProvider>
}
